import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "react-query";
import * as apiClient from "../api-client";
import { useAppContext } from "../contexts/AppContext";
import { Link, useNavigate } from "react-router-dom";
import { FaUser, FaEnvelope, FaLock, FaEye, FaEyeSlash } from 'react-icons/fa';
import { useEffect, useState } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Helmet } from "react-helmet";

export type RegisterFormData = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  isAdmin: boolean;
};

const Register = () => {

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showToast } = useAppContext();

  const {
    register,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormData>();

  const mutation = useMutation(apiClient.register, {
    onSuccess: async () => {
      showToast({ message: "Registration Success!", type: "SUCCESS" });
      await queryClient.invalidateQueries("validateToken");
      navigate("/");
    },
    onError: (error: Error) => {
      showToast({ message: error.message, type: "ERROR" });
    },
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const onSubmit = handleSubmit((data) => {
    const formDataWithAdmin = { ...data, isAdmin: false };
    mutation.mutate(formDataWithAdmin);
  });

  return (
    <>
    <HelmetProvider>
        <Helmet>
          <title>Register with Halo Holidays | Create Your Travel Profile</title>
          <meta name="description" content="Sign up with Halo Holidays to access exclusive travel deals, personalized recommendations, and manage your bookings with ease. Register today!" />
        </Helmet>
    </HelmetProvider>

    <div className="mt-20 flex items-center justify-center">
      <form
        className="bg-white p-6 md:p-10 rounded-lg shadow-md w-full max-w-lg border border-light"
        onSubmit={onSubmit}
      >
        <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center">Create an Account</h2>
        <div className="flex flex-col md:flex-row gap-5">
          <div className="flex-1">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              First Name
              <div className="flex items-center border rounded w-full py-2 px-3 mt-1">
                <FaUser className="text-halo-pink mr-2" />
                <input
                  className="border-none w-full text-gray-700 leading-tight focus:outline-none"
                  {...register("firstName", { required: "This field is required" })}
                />
              </div>
              {errors.firstName && (
                <p className="text-red-500 text-xs italic">{errors.firstName.message}</p>
              )}
            </label>
          </div>
          <div className="flex-1">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Last Name
              <div className="flex items-center border rounded w-full py-2 px-3 mt-1">
                <FaUser className="text-halo-pink mr-2" />
                <input
                  className="border-none w-full text-gray-700 leading-tight focus:outline-none"
                  {...register("lastName", { required: "This field is required" })}
                />
              </div>
              {errors.lastName && (
                <p className="text-red-500 text-xs italic">{errors.lastName.message}</p>
              )}
            </label>
          </div>
        </div>
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2">
            Email
            <div className="flex items-center border rounded w-full py-2 px-3 mt-1">
              <FaEnvelope className="text-halo-pink mr-2" />
              <input
                type="email"
                className="border-none w-full text-gray-700 leading-tight focus:outline-none"
                {...register("email", { 
                  required: "This field is required",
                  pattern: {
                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                    message: "Please enter a valid email address"
                  }
                })}
              />
            </div>
            {errors.email && (
              <p className="text-red-500 text-xs italic">{errors.email.message}</p>
            )}
          </label>
        </div>
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2">
            Password
            <div className="flex items-center border rounded w-full py-2 px-3 mt-1">
              <FaLock className="text-halo-pink mr-2" />
              <input
                type={showPassword ? "text" : "password"}
                className="border-none w-full text-gray-700 leading-tight focus:outline-none"
                {...register("password", {
                  required: "This field is required",
                  minLength: {
                    value: 6,
                    message: "Password must be at least 6 characters",
                  },
                })}
              />
              <button
                type="button"
                className="focus:outline-none"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <FaEyeSlash className="text-halo-pink" /> : <FaEye className="text-halo-pink" />}
              </button>
            </div>
            {errors.password && (
              <p className="text-red-500 text-xs italic">{errors.password.message}</p>
            )}
          </label>
        </div>
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2">
            Confirm Password
            <div className="flex items-center border rounded w-full py-2 px-3 mt-1">
              <FaLock className="text-halo-pink mr-2" />
              <input
                type={showConfirmPassword ? "text" : "password"}
                className="border-none w-full text-gray-700 leading-tight focus:outline-none"
                {...register("confirmPassword", {
                  validate: (val) => {
                    if (!val) {
                      return "This field is required";
                    } else if (watch("password") !== val) {
                      return "Your passwords do not match";
                    }
                  },
                })}
              />
              <button
                type="button"
                className="focus:outline-none"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
              >
                {showConfirmPassword ? <FaEyeSlash className="text-halo-pink" /> : <FaEye className="text-halo-pink" />}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="text-red-500 text-xs italic">{errors.confirmPassword.message}</p>
            )}
          </label>
        </div>
        <div className="flex items-center justify-center mb-4">
          <button
            type="submit"
            className="bg-halo-pink text-white py-2 px-6 rounded hover:bg-pink-800 focus:outline-none focus:shadow-outline"
            aria-label="Create Account"
          >
            Create Account
          </button>
        </div>
        <div className="flex items-center justify-center mt-4">
          <span className="text-sm">
            Already have an account?{" "}
            <Link className="underline text-pink-500" to="/sign-in">
              Sign In
            </Link>
          </span>
        </div>
      </form>
    </div>
    </>
  );
};

export default Register;
